<template>
  <lb-dialog
    v-model="dialogVisible"
    :heading="dialogTitle"
    width="480"
    persistent
    content-class="data-configuration-custom-column-dialog-shell"
    data-testid="data-configuration-custom-column-dialog"
  >
    <template #body>
      <div class="data-configuration-custom-column-dialog">
        <div class="data-configuration-custom-column-dialog__field">
          <label
            class="data-configuration-custom-column-dialog__label noTransform"
            for="custom-column-name"
          >
            Column Name
          </label>
          <v-text-field
            id="custom-column-name"
            v-model="form.columnName"
            dense
            outlined
            hide-details
            placeholder="e.g. custom_approval_flag"
            :readonly="isEditMode"
            :hint="
              isEditMode
                ? 'Column name is locked — it is the destination key.'
                : ''
            "
            :persistent-hint="isEditMode"
            class="data-configuration-custom-column-dialog__input"
            data-testid="data-configuration-custom-column-dialog__column-name"
          />
        </div>

        <div class="data-configuration-custom-column-dialog__field">
          <label
            class="data-configuration-custom-column-dialog__label noTransform"
            for="custom-column-label"
          >
            Display Label
          </label>
          <v-text-field
            id="custom-column-label"
            v-model="form.displayLabel"
            dense
            outlined
            hide-details
            placeholder="e.g. Custom Approval Flag"
            class="data-configuration-custom-column-dialog__input"
            data-testid="data-configuration-custom-column-dialog__display-label"
          />
        </div>

        <div class="data-configuration-custom-column-dialog__field">
          <label
            class="data-configuration-custom-column-dialog__label noTransform"
            for="custom-column-erp"
          >
            Map to ERP Column
          </label>
          <lb-dropdown
            id="custom-column-erp"
            v-model="form.erpColumn"
            :items="erpColumnItems"
            itemtext="label"
            itemvalue="value"
            label=""
            hidedetails
            :clearable="false"
            :searchable="true"
            appendiconinner="mdi-menu-down"
            placeholder="Select field..."
            class="data-configuration-custom-column-dialog__select"
            data-testid="data-configuration-custom-column-dialog__erp-column"
          />
        </div>

        <div class="data-configuration-custom-column-dialog__row">
          <div class="data-configuration-custom-column-dialog__field">
            <label
              class="data-configuration-custom-column-dialog__label noTransform"
              for="custom-column-type"
            >
              Data Type
            </label>
            <lb-dropdown
              id="custom-column-type"
              v-model="form.dataType"
              :items="dataTypeItems"
              itemtext="label"
              itemvalue="value"
              label=""
              hidedetails
              :clearable="false"
              :searchable="false"
              appendiconinner="mdi-menu-down"
              class="data-configuration-custom-column-dialog__select"
              data-testid="data-configuration-custom-column-dialog__data-type"
            />
          </div>

          <div class="data-configuration-custom-column-dialog__field">
            <label
              class="data-configuration-custom-column-dialog__label noTransform"
              for="custom-column-policy"
            >
              Policy
            </label>
            <lb-dropdown
              id="custom-column-policy"
              v-model="form.mask"
              :items="maskItems"
              itemtext="label"
              itemvalue="value"
              label=""
              hidedetails
              :clearable="false"
              :searchable="false"
              appendiconinner="mdi-menu-down"
              class="data-configuration-custom-column-dialog__select"
              data-testid="data-configuration-custom-column-dialog__policy"
            />
          </div>
        </div>

        <v-checkbox
          v-if="!isCanonicalEdit"
          v-model="form.required"
          hide-details
          class="data-configuration-custom-column-dialog__checkbox mt-0 pt-0"
          label="Mark as required column"
          data-testid="data-configuration-custom-column-dialog__required"
        />
      </div>
    </template>

    <template #actions>
      <base-button
        secondary
        small
        class="shadow-off"
        label="Cancel"
        data-testid="data-configuration-custom-column-dialog__cancel"
        @click="onCancel"
      />
      <base-button
        primary
        small
        class="shadow-off"
        :label="confirmLabel"
        :disabled="!canCreate"
        :data-testid="confirmTestId"
        @click="onCreate"
      />
    </template>
  </lb-dialog>
</template>

<script>
import {
  COLUMN_MASK,
  COLUMN_MASK_ITEMS,
  DATA_CONFIGURATION_DATA_TYPES,
  normalizeColumnMask,
  normalizeDropdownValue,
  slugifyColumnName,
} from '@/modules/admin/utils/dataConfigurationUtils';

const EMPTY_FORM = () => ({
  columnName: '',
  displayLabel: '',
  dataType: 'String',
  erpColumn: '',
  mask: COLUMN_MASK.NONE,
  required: false,
});

export default {
  name: 'DataConfigurationCustomColumnDialog',
  props: {
    value: { type: Boolean, default: false },
    erpColumnItems: { type: Array, default: () => [] },
    editPayload: { type: Object, default: null },
  },
  data() {
    return {
      form: EMPTY_FORM(),
      maskItems: COLUMN_MASK_ITEMS,
      nameTouched: false,
    };
  },
  computed: {
    isEditMode() {
      return !!this.editPayload;
    },
    isCanonicalEdit() {
      return !!(this.editPayload && this.editPayload.isCanonical);
    },
    dialogTitle() {
      if (this.isCanonicalEdit) return 'Edit Column Mapping';
      return this.isEditMode ? 'Edit Custom Column' : 'Create Custom Column';
    },
    confirmLabel() {
      return this.isEditMode ? 'Save Changes' : 'Create & Map';
    },
    confirmTestId() {
      return `data-configuration-custom-column-dialog__${this.isEditMode ? 'save' : 'create'}`;
    },
    dialogVisible: {
      get() {
        return this.value;
      },
      set(nextValue) {
        this.$emit('input', nextValue);
      },
    },
    dataTypeItems() {
      return DATA_CONFIGURATION_DATA_TYPES.map((type) => ({
        value: type,
        label: type,
      }));
    },
    canCreate() {
      const columnName = slugifyColumnName(this.form.columnName);
      const erpColumn = normalizeDropdownValue(this.form.erpColumn);
      return Boolean(columnName && erpColumn);
    },
  },
  watch: {
    value(isOpen) {
      if (!isOpen) return;
      if (this.editPayload) this.prefillFromEditPayload();
      else {
        this.form = EMPTY_FORM();
        this.nameTouched = false;
      }
    },
  },
  methods: {
    prefillFromEditPayload() {
      const source = this.editPayload || {};
      this.form = {
        columnName: source.columnName || '',
        displayLabel: source.displayLabel || '',
        dataType: source.dataType || 'String',
        erpColumn: source.erpColumn || null,
        mask: source.mask || COLUMN_MASK.NONE,
        required: !!source.required,
      };
      // Suppress auto-sync of Display Label from Column Name while editing.
      this.nameTouched = true;
    },
    onCancel() {
      this.dialogVisible = false;
    },
    onCreate() {
      const columnName = slugifyColumnName(this.form.columnName);
      const erpColumn = normalizeDropdownValue(this.form.erpColumn);
      const displayLabel = this.form.displayLabel.trim();

      if (!columnName) {
        this.$store.commit('sbError', 'Enter a valid column name.', { root: true });
        return;
      }
      if (!erpColumn) {
        this.$store.commit('sbError', 'Select an ERP column to map.', { root: true });
        return;
      }

      if (this.isEditMode) {
        this.$emit('save', {
          rowKey: this.editPayload.rowKey,
          columnName,
          displayLabel: displayLabel || columnName,
          dataType: this.form.dataType,
          erpColumn,
          mask: normalizeColumnMask(this.form.mask),
          required: this.form.required,
        });
      } else {
        this.$emit('create', {
          columnName,
          displayLabel: displayLabel || columnName,
          dataType: this.form.dataType,
          erpColumn,
          mask: normalizeColumnMask(this.form.mask),
          required: this.form.required,
        });
      }
      this.dialogVisible = false;
    },
  },
};
</script>

<style lang="scss" src="./DataConfigurationCustomColumnDialog.scss"></style>
